import { useEffect, useState } from 'react';
import { fetchMyPlayer } from '../lib/api/myPlayer';

/**
 * THE CONTEXT `/auth/scopes` DOES NOT CARRY — «Spiller» — for the portal's
 * context switcher (owner, 2026-10-20/21). For drawing, never for deciding.
 *
 * The organizations come from the session's own `/auth/scopes`. Whether the
 * account is also a player is asked here:
 *
 *   player     `/me/player` names a player profile. A FAILED read is YES:
 *              the player app is everybody's own, and a network blip must not
 *              take away the way back to it. A profile the server says does
 *              not exist is NO — a role must never invent a player.
 *
 * There is deliberately NO platform context. `/sa` is its own entrance and is
 * signed in with Entra (#1002); a customer session is not a way into it, so
 * nothing here asks for platform roles.
 *
 * `false` while the answer is in flight, so the switcher never draws a context
 * it then has to take back. Cleared on every account change: a second account
 * in the same tab must never read the first one's contexts.
 */
export interface RoleAvailability {
  player: boolean;
}

const NONE: RoleAvailability = { player: false };

export function useRoleAvailability(
  accountId: string | null,
  loadPlayer: typeof fetchMyPlayer = fetchMyPlayer,
): RoleAvailability {
  const [state, setState] = useState<{ accountId: string; value: RoleAvailability } | null>(null);

  useEffect(() => {
    setState(null);
    if (!accountId) return undefined;
    let live = true;
    // The loader is injectable for tests and must be a STABLE reference (the
    // module function by default): a fresh function per render re-asks.
    void loadPlayer().then(
      (playerResult) => {
        if (!live) return;
        setState({
          accountId,
          value: { player: playerResult.failed || playerResult.player !== null },
        });
      },
      () => {
        // The loader never throws by contract; an injected one that did is the
        // same «could not tell» as a failed read.
        if (live) setState({ accountId, value: { player: true } });
      },
    );
    return () => {
      live = false;
    };
  }, [accountId, loadPlayer]);

  // DERIVED, not merely remembered: on the frame an account changes, the
  // stored answer is the previous account's, and it must not be drawn.
  return state && state.accountId === accountId ? state.value : NONE;
}
